import React, { useState, useRef } from 'react'
import { Upload, CheckCircle, AlertCircle, Briefcase } from 'lucide-react'
import { getResumeAnalytics } from '../api/resume'
import { Loader } from '../components'
import { extractText } from "../utils/pdf_utils"
import { useAuth } from "../context/auth-context";
import { useNavigate } from 'react-router-dom'
import { useLocation } from 'react-router-dom';
import { log } from '../utils/log_utils'
import { removeUserCache } from '../utils/client_storage_utils'

export const HeroSection = ({ setAnalysis }) => {


  const { user } = useAuth()
  const [dragActive, setDragActive] = useState(false)
  const [errors, setErrors] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const fileInputRef = useRef()
  const [selectedFile, setSelectedFile] = useState(null)
  const [role, setRole] = useState('')
  const navigate = useNavigate();

  const handleDrag = (e) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true)
    } else if (e.type === 'dragleave') {
      setDragActive(false)
    }
  }
  const handleDrop = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileChange(e.dataTransfer.files[0])
    }
  }


  const handleChange = (e) => {
    e.preventDefault()
    if (e.target.files && e.target.files[0]) {
      handleFileChange(e.target.files[0])
    }
  }

  const handleFileChange = async (file) => {
    setAnalysis('')
    setErrors([])
    const selectedFile = file;
    if (!selectedFile || selectedFile.type !== "application/pdf"){
      alert("Please select a PDF file.");
      return
    }
    try {
      setSelectedFile(selectedFile)
      const extractedText = await extractText(selectedFile)
      log('extractedText',extractedText,'handleFileChange')
    } catch (error) {
      setErrors(e => [...e,error.message])
      log("failed to select file", selectedFile,'handleFileChange')
    }
  };

  const resetSelection = (e) =>{
    e.preventDefault()
    setSelectedFile(null)
    setAnalysis('')
    setErrors([])
    if(fileInputRef.current){
      fileInputRef.current.value = null
      fileInputRef.current = null
    }
  }

  const uploadResume = async (e) =>{
    e.preventDefault()
    try {
      setIsLoading(true)
      const resumeText = await extractText(selectedFile)
      const { success, resp } = await getResumeAnalytics(selectedFile, resumeText, role)
      log('response',resp,'uploadResume')
      if(success){
        const parsedResponse = typeof(resp) == 'string' ? JSON.parse(resp) :  resp
        log('parsedResponse',parsedResponse,'uploadResume')
        if(parsedResponse){
          setAnalysis(parsedResponse)
          setSelectedFile(null)
          return
        }
       }
       else{
        if(resp?.toLowerCase() === "invalid user identity"){
          removeUserCache()
          navigate("/")
        }
         setErrors(e => [...e, resp || 'something went wrong :(' ])
       }
    } catch (error) {
      setErrors(err => [...err,error.message])
    }
    finally{
      setIsLoading(false)
    }
  }
  
  const { pathname = ''} = useLocation();
  const handleRedirect = () =>{
    if(!user){
      // TODO: show toast erorr for logging in
    }
    else if(pathname === '/landing'){
      navigate("/")
    }

  }

  return (
    <section className="bg-gradient-to-b from-white to-indigo-50 py-20">
      <div className="container mx-auto px-4">
        <div className="flex flex-col lg:flex-row items-center ">
          <div className={`${user ? 'lg:w-1/2' : 'w-full text-center max-w-4xl mx-auto'} mb-10 lg:mb-0`}>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Unlock Your Career Potential with AI Resume Analysis
            </h1>
            <p className="text-xl text-gray-600 mb-8">
              Upload your resume, and our AI will analyze your strengths, suggest actionable improvements, 
              and generate perfectly tailored resumes for any role.
            </p>
          </div>
          {user && (
          <div className="lg:w-1/2 lg:pl-12" onClick={handleRedirect}>
            <div
              className={`border-2 border-dashed rounded-lg p-4 ${dragActive ? 'border-indigo-500 bg-indigo-50' : 'border-gray-300'} ${selectedFile ? 'bg-green-50' : ''} ${errors.length ? 'bg-red-50' : ''}`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              {/* add */}
      
              <div className={`flex flex-col items-center justify-center py-4 sm:py-10  ${ user ? 'cursor-pointer' : ''} `} >
                {!isLoading && !errors.length && (
                  <>
                  <div className="text-center my-5 flex flex-col gap-1 w-full">
                    <label htmlFor="job-role" className="text-xl font-medium text-gray-900 mb-2">
                        Job Role
                    </label>
                    <div className="relative w-full md:w-[80%] mx-auto">
                          <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                          <input
                              id="job-role"
                              type="text"
                              value={role}
                              onChange={(e) => setRole(e.target.value)}
                              placeholder="e.g., Senior Product Manager"
                              className="w-full pl-11 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                          />
                          
                    </div>
                  </div>
                    <Upload size={48} className="text-indigo-500 mb-4" />
                    <h3 className="text-xl font-medium text-gray-900 mb-2">
                      Upload Your Resume
                    </h3>
                    <p className="text-gray-500 text-center mb-6">
                      Drag and drop your resume file or click to browse
                    </p>
                    <p className="text-gray-400 text-sm mb-4">
                      Supported formats: PDF, DOCX
                    </p>
                    <input
                      ref={fileInputRef}
                      type="file"
                      id="resume-upload"
                      className="hidden"
                      onChange={handleChange}
                      accept=".pdf,.docx"
                    />
                    <p className="text-gray-600 text-sm mb-2">{selectedFile?.name}</p>
                    <label
                      htmlFor="resume-upload"
                      className="cursor-pointer bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-50 transition"
                    >
                      Select File
                    </label>
                    <div className="flex flex-col gap-2 sm:flex-row py-6">
                      <button onClick={uploadResume} className="bg-indigo-600 text-white px-6 py-3 rounded-md hover:bg-indigo-700 transition text-lg font-medium disabled:cursor-not-allowed disabled:bg-gray-500 disabled:tool" disabled={!selectedFile || !role}>
                        Analyze
                      </button>
                      <button onClick={resetSelection} className="border border-gray-600 text-gray-700 px-6 py-3 rounded-md hover:bg-gray-200 transition text-lg font-medium">
                        Reset
                      </button>
                    </div>
                  </>
                )}
                {isLoading && (
                  <div className="flex flex-col items-center">
                    <CheckCircle size={48} className="text-green-500 mb-4" />
                    <h3 className="text-xl font-medium text-gray-900 mb-2">
                      Resume Uploaded!
                    </h3>
                    <p className="text-gray-500 text-center mb-4">
                      Your resume is being analyzed by our AI
                    </p>
                    <Loader />
                  </div>
                )}
                {selectedFile && Boolean(errors.length) && (
                  <div className="flex flex-col items-center">
                    <AlertCircle size={48} className="text-red-500 mb-4" />
                    <h3 className="text-xl font-medium text-gray-900 mb-2">
                      {errors[0]}
                    </h3>
                    <p className="text-gray-500 text-center mb-4">
                      Please upload a PDF or DOCX file
                    </p>
                    <button onClick={resetSelection} className="border border-gray-600 text-gray-700 px-6 py-3 rounded-md hover:bg-gray-200 transition text-lg font-medium">
                        Reset
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
          )}
        </div>
      </div>
    </section>
  )
}
